/**
 * Breadcrumb trail — the "what happened before the bug" record. A bounded
 * in-memory ring buffer of the last N sanitized user clicks and API calls. The
 * trail is attached to error events (see rumClient.recordError) so a failure is
 * reproducible, and can be flushed on demand via `captureBreadcrumbTrail` for
 * user-reported problems with no JS exception.
 *
 * Privacy: a click breadcrumb is named ONLY from a `data-rum-id` attribute (or
 * a coarse {tag,role} fallback). It NEVER reads textContent / value /
 * aria-label / title / CSS selectors — those carry patient names on clinical
 * rows (telemetry-inventory §B / privacy-threat-model). API breadcrumbs carry
 * no URL, query, payload, response, or headers.
 *
 * Pure + DOM-env-free: the click extractor works on a minimal structural type
 * that real DOM Elements satisfy, so it is unit-testable in the node jest env.
 */
import { sanitizeStringValue } from './telemetrySanitizer';

export const DEFAULT_BREADCRUMB_LIMIT = 50;
export const DATA_RUM_ID_ATTR = 'data-rum-id';
const MAX_ANCESTOR_WALK = 5;

export interface ClickBreadcrumb {
  type: 'click';
  t: number;
  action: string;
  route?: string;
  featureArea?: string;
}

export interface ApiBreadcrumb {
  type: 'api';
  t: number;
  apiOperation: string;
  method: string;
  httpStatusGroup: string;
  durationBucket?: string;
}

export type Breadcrumb = ClickBreadcrumb | ApiBreadcrumb;

/**
 * Minimal structural shape of a clickable element. Real DOM `Element`s satisfy
 * this, so the extractor needs no jsdom to test.
 */
export interface ClickableLike {
  getAttribute(name: string): string | null;
  tagName: string;
  parentElement: ClickableLike | null;
}

/**
 * Derive a safe, low-cardinality action name for a click. Walks up to
 * MAX_ANCESTOR_WALK ancestors looking for `data-rum-id`; if none is annotated,
 * falls back to `<tag>` or `<tag>[role=...]` from the clicked element. Returns
 * a non-empty string. NEVER reads element text/labels/values.
 */
export function extractClickAction(el: ClickableLike | null): string {
  let node: ClickableLike | null = el;
  let depth = 0;
  while (node && depth < MAX_ANCESTOR_WALK) {
    const id = node.getAttribute(DATA_RUM_ID_ATTR);
    if (id) {
      const safe = sanitizeStringValue(id);
      if (safe) {
        return safe;
      }
    }
    node = node.parentElement;
    depth += 1;
  }
  if (!el) {
    return 'unknown';
  }
  const tag = (el.tagName || 'unknown').toLowerCase();
  const role = el.getAttribute('role');
  return role ? `${tag}[role=${sanitizeStringValue(role)}]` : tag;
}

/** A bounded FIFO ring buffer of breadcrumbs. Oldest entries are evicted. */
export class BreadcrumbBuffer {
  private readonly limit: number;

  private items: Breadcrumb[] = [];

  constructor(limit: number = DEFAULT_BREADCRUMB_LIMIT) {
    this.limit = Math.max(1, limit);
  }

  push(crumb: Breadcrumb): void {
    this.items.push(crumb);
    if (this.items.length > this.limit) {
      this.items.splice(0, this.items.length - this.limit);
    }
  }

  /** A copy of the current trail, oldest → newest. */
  snapshot(): Breadcrumb[] {
    return this.items.slice();
  }

  size(): number {
    return this.items.length;
  }

  clear(): void {
    this.items = [];
  }
}

/** Context attached to a click breadcrumb at capture time (already safe). */
export interface ClickContext {
  route?: string;
  featureArea?: string;
}

/** Build a click breadcrumb from an element + safe context. */
export function buildClickBreadcrumb(
  el: ClickableLike | null,
  context: ClickContext,
  now: number,
): ClickBreadcrumb {
  return {
    type: 'click',
    t: now,
    action: extractClickAction(el),
    route: context.route,
    featureArea: context.featureArea,
  };
}

/** Fields for an API breadcrumb (already normalized by the API tap). */
export interface ApiBreadcrumbInput {
  apiOperation: string;
  method: string;
  httpStatusGroup: string;
  durationBucket?: string;
}

export function buildApiBreadcrumb(input: ApiBreadcrumbInput, now: number): ApiBreadcrumb {
  return {
    type: 'api',
    t: now,
    apiOperation: input.apiOperation,
    method: input.method,
    httpStatusGroup: input.httpStatusGroup,
    durationBucket: input.durationBucket,
  };
}
